<template>
  <LsModal
    title="Cancel Subscription"
    track-id="cancel_subscription_modal_step_one"
    class="cancel-subscription-modal-step-one"
  >
    <template #body>
      <div class="cancel-subscription-modal-step-one--sections">
        <p class="cancel-subscription-modal-step-one--heading">Moving?</p>
        <p class="cancel-subscription-modal-step-one--sections-text">
          Let us know! We might be able to service you at your new property or in your new city.
          Please contact {{ brandName }} support for assistance.
        </p>
        <div
          class="cancel-subscription-modal-step-one--sections-link"
          test-id="message-ls"
          @click="navOut('messageLS')"
        >
          <LsIcon type="chevron" size="md" />
          <p class="cancel-subscription-modal-step-one--link-text">contact support</p>
        </div>
      </div>
      <div v-if="!isLtrs" class="cancel-subscription-modal-step-one--sections">
        <p class="cancel-subscription-modal-step-one--heading">Don't like your current pro?</p>
        <p class="cancel-subscription-modal-step-one--sections-text">
          We&apos;re sorry to hear if your current pro isn&apos;t a good match with your needs. But
          there&apos;s good news! We have plenty of pros in your area and can match you with a new
          one. Click below to request a new one.
        </p>
        <div
          class="cancel-subscription-modal-step-one--sections-link"
          test-id="change-my-provider"
          @click="navOut('changeMyProvider')"
        >
          <LsIcon type="chevron" size="md" />
          <p class="cancel-subscription-modal-step-one--link-text">change my pro</p>
        </div>
      </div>
      <div
        class="cancel-subscription-modal-step-one--sections"
        :class="{
          'cancel-subscription-modal-step-one--no-border': activeProperties.length < 2,
        }"
      >
        <p class="cancel-subscription-modal-step-one--heading">Still want to cancel?</p>
        <p class="cancel-subscription-modal-step-one--sections-text">
          We&apos;re sorry to see you go :(<br />Please keep us in mind for any of your future lawn
          care needs!
        </p>
      </div>
      <div
        v-if="activeProperties.length > 1"
        class="cancel-subscription-modal-step-one--sections cancel-subscription-modal-step-one--no-border"
        style="margin-top: 5px;"
      >
        <LsCheckboxes
          v-model="properties"
          value="properties"
          label="Cancel service for:"
          track-id="cancel_subscription_modal_step_one-properties"
          :options="activeProperties"
          name="properties"
        />
      </div>

      <LsInfoCard
        v-if="shouldDisplayWarning"
        test-id="cancel_warning"
        class="cancel-subscription-modal-step-one--cancel-warning"
      >
        <h4>Cannot cancel all services</h4>
        <p>
          One or more of your properties has not met our 3 cut minimum requirement. Please
          <RouterLink :to="{ name: 'messageLS' }">contact Support</RouterLink>
          for help with your cancellation request.
        </p>
      </LsInfoCard>
    </template>
    <template #footer>
      <LsButton
        text="cancel services"
        test-id="cancel_services"
        track-id="cancel_subscription_modal-cancel"
        :disabled="!properties.length"
        @click="nextStep"
      />
    </template>
  </LsModal>
</template>

<script>
import { LsButton, LsIcon, LsModal, LsCheckboxes, LsInfoCard } from '@/components/common';
import { mapGetters, mapState } from 'vuex';
import _ from 'lodash';
import { PROPERTIES__PROPERTIES_IN_ARRAY, SCHEDULES__GROUPED_BY_PROPERTY } from '@/store/types';
import VueTypes from 'vue-types';
import { themedComponentMixin } from '@/mixins';
import { lsScheduleHelpers, lsPropertyHelpers } from '@/helpers';

export default {
  name: 'CancelSubscriptionModalStepOne',
  components: {
    LsButton,
    LsIcon,
    LsModal,
    LsCheckboxes,
    LsInfoCard,
  },
  mixins: [themedComponentMixin],
  props: {
    next: VueTypes.func.isRequired,
    isLtrs: VueTypes.bool.def(false),
    serviceId: VueTypes.number.isRequired,
  },
  data() {
    return {
      properties: [],
    };
  },
  computed: {
    ...mapState(['support']),
    ...mapGetters([PROPERTIES__PROPERTIES_IN_ARRAY, SCHEDULES__GROUPED_BY_PROPERTY]),
    shouldDisplayWarning() {
      return _.some(this.activeProperties, ({ disabled }) => disabled);
    },
  },
  created() {
    this.activeProperties = _.map(
      this[SCHEDULES__GROUPED_BY_PROPERTY],
      this.findActiveScheduleByServiceId
    )
      .filter((schedule) => !!schedule)
      .map(this.mapMowingSchedule)
      .filter(this.filterActiveMowing)
      .map(this.hasCancellableMowing);
  },
  methods: {
    nextStep() {
      this.next({ properties: this.properties });
    },
    navOut(name) {
      this.$router.push({
        name,
      });

      this.$parent.$parent.close();
    },
    findActiveScheduleByServiceId(schedules) {
      return lsScheduleHelpers.findActiveScheduleByServiceId({
        schedules,
        serviceId: this.serviceId,
      });
    },
    mapMowingSchedule(schedule) {
      return {
        property: this[PROPERTIES__PROPERTIES_IN_ARRAY].find(
          (property) => property.property_id === schedule.property_id
        ),
        isMowing: lsScheduleHelpers.isServiceMowing(schedule),
      };
    },
    filterActiveMowing({ property, isMowing }) {
      return !isMowing || !_.isNil(property.active_mowing_schedule_id);
    },
    hasCancellableMowing({ property, isMowing }) {
      const canCancel = !isMowing || lsPropertyHelpers.hasCancellableMowing({ property });
      if (canCancel) {
        this.properties.push(property.property_id);
      }
      return {
        label: property.street,
        value: canCancel ? property.property_id : false,
        disabled: !canCancel,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.cancel-subscription-modal-step-one--sections {
  padding: 10px 0;
  border-bottom: 2px solid $primary-light;
}

.cancel-subscription-modal-step-one--no-border {
  border: none;
}

.cancel-subscription-modal-step-one--heading {
  padding: 10px 0;
  font-weight: bold;
}

.cancel-subscription-modal-step-one--sections-link {
  padding: 14px 0 8px 0;
  display: inline-flex;
  cursor: pointer;
  user-select: none;
}

.cancel-subscription-modal-step-one--link-text {
  padding: 1px 0 0 2px;
  text-transform: uppercase;
  font-weight: bold;
  color: $primary;
}
.themed-app {
  .cancel-subscription-modal-step-one--sections {
    border-color: $theme-color-primary-light;
  }
  .cancel-subscription-modal-step-one--link-text {
    color: $theme-color-primary;
  }
}
</style>

<style lang="scss">
.cancel-subscription-modal-step-one {
  .cancel-subscription-modal-step-one--cancel-warning {
    align-items: flex-start;
    flex-direction: column;

    & > h4 {
      font-size: 1.4rem;
      font-weight: bold;
      text-transform: uppercase;
    }
  }
}
</style>
